// src/utils/suiviNcs.ts — SCE Issues
// Où en est une NC-S chez la qualité (06/10, piste B validée par Guillaume) :
// le badge NC-S de la fiche dit son état (Ouverte, Terminée, Annulée), et un
// clic montre le détail — composant, fournisseur, pilote, décision,
// avancement. Rien dans l'activité : « le suivi qualité est interne à eux ».
//
// La source est le registre de la qualité (colonnes de suivi lues avec le
// N°, utils/registreNcs). Tout le monde n'a pas accès au site QM_SCE : quand
// quelqu'un qui y a accès lit le registre, l'appli range un instantané des
// NC-S de ses problèmes dans le bloc `suiviNcs` de la ligne `__global__` de
// SCE_UserPrefs — sans colonne ni liste à créer — et tous le lisent.
// Les commentaires internes et les contacts fournisseur ne sont PAS repris.
//
// Pur : aucun React, aucun réseau.

import type { LigneRegistre } from '@/utils/registreNcs'

export interface SuiviNcs {
  numero: string
  /** NC Status du registre, brut : Open, Completed, Cancelled. */
  statut: string
  fournisseur: string
  pays: string
  pilote: string
  /** NC Legitimacy : Accepted, Not accepted, Goodwill. */
  decision: string
  /** Type of correction : Rework / Repair, Reject / Replacement, Use as is, Other. */
  correction: string
  /** Containment & Correction Status. */
  avancement: string
  /** RCA Status (or 8D status for NC-S). */
  rca: string
  /** NC Report Closure Date, `AAAA-MM-JJ`. */
  clotureLe: string | null
  nvs: string[]
  designation: string
  quantite: string
}

/** Le bloc `suiviNcs` de la ligne globale. */
export interface BlocSuiviNcs {
  version: 1
  /** Dernière lecture du registre qui l'a changé, ISO. */
  majLe: string
  ncs: Record<string, SuiviNcs>
}

/** Valeurs vides du registre (« #N/A », « N/A », « NA »…) : rien. */
function propre(v: string): string {
  const t = v.trim()
  return /^(#?N\/?A|N\/?K|TBD|TBA|-)$/i.test(t) ? '' : t
}

export function suiviDepuisLigne(l: LigneRegistre): SuiviNcs {
  return {
    numero: l.numero,
    statut: propre(l.statut ?? ''),
    fournisseur: propre(l.fournisseur),
    pays: propre(l.pays ?? ''),
    pilote: propre(l.pilote ?? ''),
    decision: propre(l.decision ?? ''),
    correction: propre(l.correction ?? ''),
    avancement: propre(l.avancement ?? ''),
    rca: propre(l.rca ?? ''),
    clotureLe: l.clotureLe ?? null,
    nvs: l.nvs,
    designation: propre(l.designation),
    quantite: propre(l.quantite ?? ''),
  }
}

/**
 * Le bloc mis à jour avec les NC-S `numeros` trouvées dans le registre — null
 * si rien n'a changé (aucune écriture inutile). Les NC-S absentes du registre
 * gardent leur dernier instantané.
 */
export function blocSuiviMisAJour(
  actuel: BlocSuiviNcs | null,
  registre: readonly LigneRegistre[],
  numeros: ReadonlySet<string>,
  maintenant: Date,
): BlocSuiviNcs | null {
  const ncs: Record<string, SuiviNcs> = { ...(actuel?.ncs ?? {}) }
  let change = false
  for (const l of registre) {
    if (!numeros.has(l.numero)) continue
    const s = suiviDepuisLigne(l)
    if (JSON.stringify(ncs[l.numero]) !== JSON.stringify(s)) {
      ncs[l.numero] = s
      change = true
    }
  }
  return change ? { version: 1, majLe: maintenant.toISOString(), ncs } : null
}

/** Relit le bloc tel que stocké ; null s'il est absent ou d'une autre forme. */
export function lireBlocSuivi(brut: unknown): BlocSuiviNcs | null {
  if (!brut || typeof brut !== 'object') return null
  const b = brut as Partial<BlocSuiviNcs>
  return b.version === 1 && b.ncs && typeof b.ncs === 'object' ? (b as BlocSuiviNcs) : null
}

// ─── En français, pour la fiche ───

export type EtatNcs = 'attente' | 'enregistree' | 'ouverte' | 'terminee' | 'annulee'

/** L'état affiché sur le badge : sans N°, en attente ; sans suivi, enregistrée. */
export function etatNcs(ref: string | undefined, suivi: SuiviNcs | undefined): EtatNcs {
  if (!ref) return 'attente'
  const s = (suivi?.statut ?? '').toLowerCase()
  if (s.startsWith('complet') || s === 'closed') return 'terminee'
  if (s.startsWith('cancel')) return 'annulee'
  if (s) return 'ouverte'
  return 'enregistree'
}

export const LIBELLES_ETAT_NCS: Record<EtatNcs, string> = {
  attente: 'N° en attente',
  enregistree: 'Enregistrée',
  ouverte: 'Ouverte',
  terminee: 'Terminée',
  annulee: 'Annulée',
}

const TRADUCTIONS: Readonly<Record<string, string>> = {
  accepted: 'Acceptée',
  'not accepted': 'Refusée',
  goodwill: 'Geste commercial',
  'rework / repair': 'Retouche / réparation',
  'reject / replacement': 'Rebut / remplacement',
  'use as is': 'Utilisée en l’état',
  other: 'Autre',
  completed: 'terminée',
  'not started': 'pas démarrée',
  'in progress': 'en cours',
  ongoing: 'en cours',
  'not required': 'non requis',
  'waiting for items': 'en attente des pièces',
}

/** Traduit une valeur connue du registre ; une autre passe telle quelle. */
export function enFrancais(v: string): string {
  return TRADUCTIONS[v.trim().toLowerCase()] ?? v.trim()
}

/** « Acceptée · Retouche / réparation » */
export function decisionLisible(s: SuiviNcs): string {
  return [s.decision, s.correction].filter(Boolean).map(enFrancais).join(' · ')
}

/** « Correction terminée · 8D non requis » */
export function avancementLisible(s: SuiviNcs): string {
  return [
    s.avancement && `Correction ${enFrancais(s.avancement).toLowerCase()}`,
    s.rca && `8D ${enFrancais(s.rca).toLowerCase()}`,
  ]
    .filter(Boolean)
    .join(' · ')
}
